import { FC } from "react"
import { Button, SimpleModal } from "@workspace/ui"
import { BreakableEmail } from "./app-tile"
import { BLUE_BUTTON, BLUE_STYLE, GHOST_BUTTON } from "./modal-buttons"
import { DashboardTileDef, SessionHolder, holderLine } from "./tiles"

type SeatsFullModalProps = {
  tile: DashboardTileDef
  holders: SessionHolder[]
  /** The team's limit in the app (-1 or absent when the API sent none). */
  limit?: number
  loading: boolean
  error: string | null
  onCloseOthers: () => void
  onClose: () => void
}

/**
 * A tap on a locked tile: every one of the team's seats in the app is taken
 * (client 2026-11-04, as in-game). The dialog says who is on and offers to
 * close the other sessions — teammates' and the user's own elsewhere — so
 * this device can take a seat. Closing is not signing out: those devices go
 * back to the dashboard, still signed in; only a coach can sign themselves
 * out.
 */
export const SeatsFullModal: FC<SeatsFullModalProps> = ({ tile, holders, limit, loading, error, onCloseOthers, onClose }) => {
  const seats =
    typeof limit === "number" && limit > 0
      ? `All ${limit} ${tile.title} ${limit === 1 ? "session" : "sessions"}`
      : `All ${tile.title} sessions`
  return (
    <SimpleModal
      isOpen
      onClose={loading ? () => {} : onClose}
      title={`${tile.title} is in use`}
      showCloseButton={!loading}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={loading} className={GHOST_BUTTON}>
            Cancel
          </Button>
          <Button variant="default" onClick={onCloseOthers} disabled={loading} className={BLUE_BUTTON} style={BLUE_STYLE}>
            {loading ? "Closing..." : "Close Other Sessions"}
          </Button>
        </div>
      }
    >
      <div className="w-full text-left">
        <p className="text-muted-foreground text-sm">
          {seats} are in use. Close the other sessions to continue in this device (this action will take other users
          back to the dashboard).
        </p>
        {holders.length > 0 ? (
          <div className="mt-3 text-sm text-[#6B7280]">
            <div className="font-semibold text-[#374151]">Current Sessions:</div>
            {holders.map((holder) => (
              <div key={holder.label} className="break-words">
                <BreakableEmail text={holderLine(holder)} />
              </div>
            ))}
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="mt-2 text-sm text-red-600">
            {error}
          </p>
        ) : null}
      </div>
    </SimpleModal>
  )
}
